/* eslint-disable @nx/enforce-module-boundaries */
import { Badge, Box, Flex } from '@mybridge/ui';
import moment from 'moment';
import React, {useEffect, useMemo, useState} from 'react';
import Calendar from 'react-calendar';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useNumberFormatter } from '@mybridge/ui/custom-hooks/useNumberFormatter';
import {
  getAllEvent,
  getAllMeetings,
  getEventsFromGoogle,
} from 'v4/store/actions/calendar.actions';
import 'moment/min/locales';

const getDateKey = (date) => moment(date).local().format('YYYY-MM-DD');

const hasPendingAttendee = (attendees = []) =>
  attendees?.some((attendee) =>
    ['PENDING', 'NEEDS_ACTION', 'NEEDSACTION', 'TENTATIVE'].includes(
      String(attendee?.status || attendee?.responseStatus || '').toUpperCase()
    )
  );

const hasDeclinedAttendee = (attendees = [], userId) =>
  attendees?.some((attendee) => attendee?.user === userId && attendee?.status === 'DECLINED');

const stableId = (prefix, sourceId) =>
  `${prefix || 'ev'}-${String(sourceId ?? '').replace(/\s/g, '')}`;

const myBridgeItemId = (sourceId) => stableId('mybridge', sourceId);

export const DashboardCalendar = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [value, onChange] = useState(new Date());
  const { t } = useTranslator();
  const {formatNumber} = useNumberFormatter();
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const {
    allCalendarMeeting,
    allCalendarEvents,
    allGoogleMeetings,
  } = useSelector((state) => state.calendar);

  useEffect(() => {
    if (typeof window === 'undefined' || !localStorage.getItem('access')) return;

    const startDate = moment().locale('en').format('YYYY-MM-DD') + 'T00:00:00';
    const endDate = moment().locale('en').add(60, 'days').format('YYYY-MM-DD') + 'T23:59:00';

    dispatch(getAllMeetings({ startDate, endDate }));
    dispatch(getAllEvent());

    if (userProfileInfo?.google_calendar_email) dispatch(getEventsFromGoogle());
  }, [dispatch, userProfileInfo?.google_calendar_email]);

  const calendarIndicators = useMemo(() => {
    const indicators = {};
    const events = [];
    const myBridgeEventsIds = new Set();

    const addEvent = (event) => {
      if (!event?.id || !event?.start) return;
      events.push(event);
    };

    const addIndicator = (event) => {
      const parsedDate = new Date(event?.start);
      if (isNaN(parsedDate.getTime())) return;

      const key = getDateKey(parsedDate);
      indicators[key] = indicators[key] || {
        total: 0,
        meeting: 0,
        event: 0,
        pending: 0,
      };
      indicators[key].total += 1;
      indicators[key][event.eventType] += 1;
      if (event.isPending) indicators[key].pending += 1;
    };

    allCalendarMeeting?.results?.forEach((meeting) => {
      if (hasDeclinedAttendee(meeting?.attendees, userProfileInfo?.id)) return;

      myBridgeEventsIds.add(String(meeting?.id));
      addEvent({
        id: myBridgeItemId(meeting?.id),
        start: meeting?.start_time,
        eventType: 'meeting',
        isPending: hasPendingAttendee(meeting?.attendees),
      });
    });

    allCalendarEvents?.results?.forEach((meeting) => {
      if (hasDeclinedAttendee(meeting?.attendees, userProfileInfo?.id)) return;

      const isMyMeeting =
        meeting?.source === 'mybridge' || meeting?.created_by?.id === userProfileInfo?.id;

      myBridgeEventsIds.add(String(meeting?.id));
      addEvent({
        id: myBridgeItemId(meeting?.id),
        start: meeting?.start_time,
        eventType: isMyMeeting ? 'meeting' : 'event',
        isPending: hasPendingAttendee(meeting?.attendees),
      });
    });

    allGoogleMeetings?.items?.forEach((meeting) => {
      if (
        myBridgeEventsIds.has(String(meeting?.id?.slice(6))) ||
        meeting?.source !== 'google'
      ) {
        return;
      }

      addEvent({
        id: stableId('google', meeting?.id),
        start: meeting?.start?.dateTime || meeting?.start?.date,
        eventType: 'meeting',
        isPending: hasPendingAttendee(meeting?.attendees),
      });
    });

    const dedupedEvents = new Map();
    events.forEach((event) => dedupedEvents.set(event.id, event));
    dedupedEvents.forEach(addIndicator);

    return indicators;
  }, [allCalendarEvents, allCalendarMeeting, allGoogleMeetings, userProfileInfo?.id]);

  const renderTileIndicator = ({ date, view }) => {
    if (view !== 'month') return null;

    // Only show indicators for today and upcoming dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return null;

    const dayIndicators = calendarIndicators[getDateKey(date)];
    if (!dayIndicators) return null;

    // For today, show a white dot instead of colored indicators
    const isToday = date.getTime() === today.getTime();
    if (isToday) {
      return (
        <Box display="flex" title={`${dayIndicators.total} calendar item${dayIndicators.total > 1 ? 's' : ''}`} justifyContent="center" alignItems="center" mt="1px">
          <Flex gap={1} alignItems="center">
            {dayIndicators.total > 1 ? (
              <Badge border='1px solid #FFFFFF' fontSize="10px" fontWeight="700" lineHeight='1' color="#FFFFFF" bg="#FF0600" borderRadius="full" p='3px' w="19px" textAlign="center">
                {formatNumber(dayIndicators.total)}
              </Badge>
            ) : (
              <Box w="7px" h="7px" borderRadius="50%" bg="#FFFFFF" />
            )}
          </Flex>
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="2px"
        mt="1px"
        title={`${dayIndicators.total} calendar item${dayIndicators.total > 1 ? 's' : ''}`}
        aria-label={`${dayIndicators.total} calendar item${dayIndicators.total > 1 ? 's' : ''}`}
      >
        {dayIndicators.meeting > 0 && (
          <Box w="7px" h="7px" borderRadius="50%" bg="#037F29" />
        )}
        {dayIndicators.event > 0 && (
          <Box w="7px" h="7px" borderRadius="50%" bg="#2c7bcf" />
        )}
        {dayIndicators.pending > 0 && (
          <Box w="7px" h="7px" borderRadius="50%" bg="#f7a02f" />
        )}
        {dayIndicators.total > 1 && (
          <Box as="span" fontSize="10px" fontWeight="600" lineHeight="1" color="#4A5568">
            {formatNumber(dayIndicators.total)}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box pt={2}>
      <Calendar 
        prev2Label={null} 
        next2Label={null} 
        onChange={(e) => {
          localStorage.setItem('dateSelectedFromDashboard', e.toISOString());
          router.push('/profile/calendar')
        }}
        value={value}
        tileContent={renderTileIndicator}
        // Add translations for calendar navigation
        navigationLabel={({ date }) => 
          `${t(`myCalendar.months.${date.toLocaleString('default', { month: 'long' }).toLowerCase()}`)} ${formatNumber(date.getFullYear())}`
        }
        // Add translations for weekday names
        formatShortWeekday={(locale, date) => 
          t(`myCalendar.shortDays.${date.toLocaleString('default', { weekday: 'short' }).toLowerCase()}`)
        }
         // Localize date values using formatNumber
        formatDay={(locale, date) => 
          formatNumber(date.getDate(), { locale })
        }
      />
    </Box>
  );
};
